import { ComponentProps } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type HighlightsSubLeadProps = {
  /** The classname of the component. */
  className?: string;
  /** The article to be displayed as the lead highlight. */
  cardProps: ComponentProps<typeof Card>[];
};

/** This is the next 6 articles after the lead in the highlights component.
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79061&m=dev
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79072&m=dev
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79083&m=dev
 */
const HighlightsSubLead = ({
  className,
  cardProps,
}: HighlightsSubLeadProps): React.ReactElement => {
  const { isScreenMD, isScreenXL } = useWindowSize();

  const variant: CardVariations = {
    image: {
      position: "left",
      width: cn("w-120px"),
    },
    content: {
      width: cn("w-full-120px"),
      extraClass: cn("md:pl-4"),
    },
    title: {
      color: cn("text-gray-850"),
      size: cn("text-base"),
      extraClass: cn("font-semibold leading-[1.25rem] font-lct"),
    },
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
  };
  const group = () => {
    if (isScreenXL) return 2;

    return 3;
  };

  const groupedArticles = new Array(4)
    // Make sure each element isn't empty
    .fill("")
    // For each group, grab the right `slice` of the input array
    .map((_, i) => cardProps.slice(i * group(), (i + 1) * group()));

  return (
    <div
      className={cn("grid gap-8", className)}
      data-testid="highlights-sub-lead-component"
    >
      {groupedArticles.map((_cardprop, index) => {
        return (
          <div
            key={index}
            className={cn(
              "relative grid gap-8",
              "lg:grid-cols-3",
              "lg:after:absolute lg:after:-bottom-4 lg:after:h-[1px] lg:after:w-full lg:after:bg-gray-175",
              "xl:grid-cols-2"
            )}
          >
            {_cardprop.map((cardProp, _index) => {
              return (
                <Card
                  key={cardProp.id}
                  {...cardProp}
                  variations={variant}
                  thumbnail={isScreenMD ? cardProp.thumbnail : undefined}
                  className={cn(
                    "relative after:absolute after:-bottom-4 after:h-[1px] after:w-full after:bg-gray-175 lg:after:hidden",
                    {
                      "lg:before:absolute lg:before:-left-4 lg:before:h-full lg:before:w-[1px] lg:before:bg-gray-175":
                        _index >= 1,
                    }
                  )}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default HighlightsSubLead;
